import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Star, Calendar, User, FileText, Eye, X } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import '../styles/StoryViewPage.css';

const StoryViewPage = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArtworkModal, setShowArtworkModal] = useState(false);

  useEffect(() => {
    loadStory();
  }, [storyId]);

  const loadStory = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('artspeaks_stories')
        .select(`
          story_id, title, story_text, story_type, featured, created_at, updated_at,
          story_writer:users!artist_uuid (uuid, nickname, first_name, last_name, email, bio, profile_img),
          artwork:vcs!vc_id (
            vc_id, title, cover_image, description,
            visual_artist:users!uuid (uuid, nickname, first_name, last_name, email, bio, profile_img)
          ),
          event:events!event_id (id, name, event_start, event_end),
          collection:collections!collection_id (id, title, description)
        `)
        .eq('story_id', storyId)
        .single();

      if (error) throw error;
      if (!data) {
        setError('Story not found');
        return;
      }

      // Transform the data
      const transformedStory = {
        ...data,
        content: data.story_text || '',
        story_writer_name: data.story_writer ? 
          (data.story_writer.nickname || `${data.story_writer.first_name} ${data.story_writer.last_name}`) : 
          'Unknown Writer',
        visual_artist_name: data.artwork?.visual_artist ?
          (data.artwork.visual_artist.nickname || `${data.artwork.visual_artist.first_name} ${data.artwork.visual_artist.last_name}`) :
          'Unknown Visual Artist',
        artwork_title: data.artwork?.title || 'Unknown Artwork',
        event_name: data.event?.name || 'No Event',
        collection_title: data.collection?.title || 'No Collection'
      };

      setStory(transformedStory);
    } catch (error) {
      console.error('Error loading story:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/stories/${storyId}/edit`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="story-view-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading story...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="story-view-page">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={handleBack} className="btn-primary">
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="story-view-page">
        <div className="error-state">
          <h2>Story Not Found</h2>
          <p>The requested story could not be found.</p>
          <button onClick={handleBack} className="btn-primary">
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="story-view-page">
      {/* Header */}
      <div className="story-header">
        <div className="header-left">
          <button onClick={handleBack} className="back-btn">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="story-title-section">
            <h1 className="story-title">{story.title}</h1>
            <div className="story-meta">
              <span className="story-type">{story.story_type}</span>
              {story.featured && (
                <span className="featured-badge">
                  <Star className="w-3 h-3" />
                  Featured
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={handleEdit} className="btn-secondary">
            <Edit className="w-4 h-4" />
            Edit Story
          </button>
        </div>
      </div>

      {/* Story Content */}
      <div className="story-content">
        <div className="content-section">
          <h3>Story Content</h3>
          
          {/* Artwork Thumbnail */}
          {story.artwork?.cover_image && (
            <div className="story-artwork-thumb">
              <img 
                src={story.artwork.cover_image} 
                alt={story.artwork.title}
                onClick={() => setShowArtworkModal(true)}
                className="artwork-thumbnail"
              />
              <div className="artwork-thumb-info">
                <span className="artwork-thumb-title">{story.artwork.title}</span>
                <span className="artwork-thumb-artist">by {story.visual_artist_name}</span>
              </div>
            </div>
          )}
          
          <div className="story-text">
            {story.content || 'No content available'}
          </div>
        </div>

        {/* Story Details */}
        <div className="story-details">
          <div className="detail-section">
            <h3>Story Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <Calendar className="detail-icon" />
                <div className="detail-content">
                  <span className="detail-label">Created</span>
                  <span className="detail-value">
                    {new Date(story.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              
              <div className="detail-item">
                <User className="detail-icon" />
                <div className="detail-content">
                  <span className="detail-label">Story Writer</span>
                  <span className="detail-value">{story.story_writer_name}</span>
                </div>
              </div>

              <div className="detail-item">
                <FileText className="detail-icon" />
                <div className="detail-content">
                  <span className="detail-label">Artwork</span>
                  <span className="detail-value">{story.artwork_title}</span>
                </div>
              </div>

              <div className="detail-item">
                <User className="detail-icon" />
                <div className="detail-content">
                  <span className="detail-label">Visual Artist</span>
                  <span className="detail-value">{story.visual_artist_name}</span>
                </div>
              </div>

              <div className="detail-item">
                <Eye className="detail-icon" />
                <div className="detail-content">
                  <span className="detail-label">Event</span>
                  <span className="detail-value">{story.event_name}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Story Writer Information */}
          {story.story_writer && (
            <div className="detail-section">
              <h3>Story Writer Information</h3>
              <div className="artist-info">
                <div className="artist-avatar">
                  {story.story_writer.profile_img ? (
                    <img src={story.story_writer.profile_img} alt={story.story_writer_name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {story.story_writer_name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="artist-details">
                  <h4>{story.story_writer_name}</h4>
                  <p className="artist-email">{story.story_writer.email}</p>
                  {story.story_writer.bio && (
                    <p className="artist-bio">{story.story_writer.bio}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Visual Artist Information */}
          {story.artwork?.visual_artist && (
            <div className="detail-section">
              <h3>Visual Artist Information</h3>
              <div className="artist-info">
                <div className="artist-avatar">
                  {story.artwork.visual_artist.profile_img ? (
                    <img src={story.artwork.visual_artist.profile_img} alt={story.visual_artist_name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {story.visual_artist_name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="artist-details">
                  <h4>{story.visual_artist_name}</h4>
                  <p className="artist-email">{story.artwork.visual_artist.email}</p>
                  {story.artwork.visual_artist.bio && (
                    <p className="artist-bio">{story.artwork.visual_artist.bio}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Artwork Information */}
          {story.artwork && (
            <div className="detail-section">
              <h3>Artwork Information</h3>
              <div className="artwork-info">
                {story.artwork.cover_image && (
                  <div className="artwork-image">
                    <img src={story.artwork.cover_image} alt={story.artwork.title} />
                  </div>
                )}
                <div className="artwork-details">
                  <h4>{story.artwork.title}</h4>
                  {story.artwork.description && (
                    <p className="artwork-description">{story.artwork.description}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Artwork Modal */}
      {showArtworkModal && story.artwork && (
        <div className="artwork-modal-overlay" onClick={() => setShowArtworkModal(false)}>
          <div className="artwork-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="artwork-modal-header">
              <h3>{story.artwork.title}</h3>
              <button 
                className="artwork-modal-close"
                onClick={() => setShowArtworkModal(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="artwork-modal-body">
              <img 
                src={story.artwork.cover_image} 
                alt={story.artwork.title}
                className="artwork-modal-image"
              />
              <div className="artwork-modal-info">
                <h4>by {story.visual_artist_name}</h4>
                {story.artwork.description && (
                  <p className="artwork-description">{story.artwork.description}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryViewPage;
